import React from "react";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
} from "lucide-react";
import { NoWrapIcon, WrapIcon, WrapReverseIcon } from "../icons";
import {
  AlignContentCenterIcon,
  AlignContentFlexEndIcon,
  AlignContentFlexStartIcon,
  AlignContentSpaceAroundIcon,
  AlignContentSpaceBetweenIcon,
  AlignContentStretchIcon,
  JustifyCenterIcon,
  JustifyFlexEndIcon,
  JustifyFlexStartIcon,
  JustifySpaceBetweenIcon,
  JustifySpaceEvenlyIcon,
} from "../icons";
import { StrikethroughIcon } from "../icons";
import {
  TextAlignCenterIcon,
  TextAlignJustifyIcon,
  TextAlignLeftIcon,
  TextAlignRightIcon,
} from "@radix-ui/react-icons";

export const FLEX_DIRECTION_OPTIONS = [
  {
    key: "column",
    option: {
      tailwind: "flex-col",
      css: "flex-direction: column",
    },
    labelContent: <ArrowDownIcon width={15} height={15} />,
    tooltipContent: "Vertical layout",
  },
  {
    key: "column-reverse",
    option: {
      tailwind: "flex-col-reverse",
      css: "flex-direction: column-reverse",
    },
    labelContent: <ArrowUpIcon width={15} height={15} />,
    tooltipContent: "Vertical layout reversed",
  },
  {
    key: "row",
    option: {
      tailwind: "flex-row",
      css: "flex-direction: row",
    },
    labelContent: <ArrowRightIcon width={15} height={15} />,
    tooltipContent: "Horizontal layout",
  },
  {
    key: "row-reverse",
    option: {
      tailwind: "flex-row-reverse",
      css: "flex-direction: row-reverse",
    },
    tooltipContent: "Horizontal layout reversed",
    labelContent: <ArrowLeftIcon width={15} height={15} />,
  },
];

export const FLEX_WRAP_OPTIONS = [
  {
    key: "nowrap",
    option: {
      tailwind: "flex-nowrap",
      css: "flex-wrap: nowrap",
    },
    labelContent: <NoWrapIcon width={16} height={16} strokeWidth={3} />,
    tooltipContent: "No wrap",
  },
  {
    key: "wrap",
    option: {
      tailwind: "flex-wrap",
      css: "flex-wrap: wrap",
    },
    labelContent: <WrapIcon width={16} height={16} />,
    tooltipContent: "Wrap",
  },
  {
    key: "wrap-reverse",
    option: {
      tailwind: "flex-wrap-reverse",
      css: "flex-wrap: wrap-reverse",
    },
    labelContent: <WrapReverseIcon width={16} height={16} />,
    tooltipContent: "Wrap reversed",
  },
];

export const ALIGN_CONTENT_OPTIONS = [
  {
    key: "flex-start",
    option: {
      tailwind: "content-start",
      css: "align-content: flex-start;",
    },
    labelContent: <AlignContentFlexStartIcon width={16} height={16} />,
    tooltipContent: "Align content start",
  },
  {
    key: "center",
    option: {
      tailwind: "content-center",
      css: "align-content: center;",
    },
    labelContent: <AlignContentCenterIcon width={16} height={16} />,
    tooltipContent: "Align content center",
  },
  {
    key: "flex-end",
    option: {
      tailwind: "content-end",
      css: "align-content: flex-end;",
    },
    labelContent: <AlignContentFlexEndIcon width={16} height={16} />,
    tooltipContent: "Align content end",
  },
  {
    key: "stretch",
    option: {
      tailwind: "content-stretch",
      css: "align-content: stretch;",
    },
    labelContent: <AlignContentStretchIcon width={18} height={18} />,
    tooltipContent: "Align content stretch",
  },
  {
    key: "space-between",
    option: {
      tailwind: "content-between",
      css: "align-content: space-between;",
    },
    labelContent: <AlignContentSpaceBetweenIcon width={18} height={18} />,
    tooltipContent: "Align content space between",
  },
  {
    key: "space-around",
    option: {
      tailwind: "content-around",
      css: "align-content: space-around;",
    },
    labelContent: <AlignContentSpaceAroundIcon width={16} height={16} />,
    tooltipContent: "Align content space around",
  },
];

export const JUSTIFY_CONTENT_OPTIONS = [
  {
    key: "flex-start",
    option: {
      tailwind: "justify-start",
      css: "justify-content: flex-start;",
    },
    labelContent: <JustifyFlexStartIcon width={18} height={18} />,
    tooltipContent: "Justify content start",
  },
  {
    key: "center",
    option: {
      tailwind: "justify-center",
      css: "justify-content: center;",
    },
    labelContent: <JustifyCenterIcon width={18} height={18} />,
    tooltipContent: "Justify content center",
  },
  {
    key: "flex-end",
    option: {
      tailwind: "justify-end",
      css: "justify-content: flex-end;",
    },
    labelContent: <JustifyFlexEndIcon width={18} height={18} />,
    tooltipContent: "Justify content end",
  },
  {
    key: "space-between",
    option: {
      tailwind: "justify-between",
      css: "justify-content: space-between;",
    },
    labelContent: <JustifySpaceBetweenIcon width={18} height={18} />,
    tooltipContent: "Justify content between",
  },
  {
    key: "space-evenly",
    option: {
      tailwind: "justify-evenly",
      css: "justify-content: space-evenly;",
    },
    labelContent: <JustifySpaceEvenlyIcon width={18} height={18} />,
    tooltipContent: "Justify content evenly",
  },
];

export const TEXT_ALIGN_OPTIONS = [
  {
    key: "left",
    option: {
      tailwind: "text-left",
      css: "text-align: left",
    },
    labelContent: <TextAlignLeftIcon />,
    tooltipContent: "Text align left",
  },
  {
    key: "center",
    option: {
      tailwind: "text-center",
      css: "text-align: center",
    },
    labelContent: <TextAlignCenterIcon />,
    tooltipContent: "Text align center",
  },
  {
    key: "right",
    option: {
      tailwind: "text-right",
      css: "text-align: right",
    },
    labelContent: <TextAlignRightIcon />,
    tooltipContent: "Text align right",
  },
  {
    key: "justify",
    option: {
      tailwind: "text-justify",
      css: "text-align: justify",
    },
    labelContent: <TextAlignJustifyIcon />,
    tooltipContent: "Text align justified",
  },
];

export const TEXT_DECORATION_LINE_OPTIONS = [
  {
    key: "underline",
    option: {
      tailwind: "underline",
      css: "text-decoration-line: underline",
    },
    labelContent: (
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M4 12C4 13.1046 5.34315 14 8 14C10.6569 14 12 13.1046 12 12V2"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <line
          x1="4"
          y1="14.5"
          x2="12"
          y2="14.5"
          stroke="currentColor"
          strokeWidth="1.5"
        />
      </svg>
    ),
    tooltipContent: "Underline",
  },
  {
    key: "line-through",
    option: {
      tailwind: "line-through",
      css: "text-decoration-line: line-through",
    },
    labelContent: <StrikethroughIcon />,
    tooltipContent: "Line Through",
  },
  {
    key: "overline",
    option: {
      tailwind: "overline",
      css: "text-decoration-line: overline",
    },
    labelContent: (
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <line
          x1="4"
          y1="2.5"
          x2="12"
          y2="2.5"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <path
          d="M4 4C4 5.10457 5.34315 6 8 6C10.6569 6 12 5.10457 12 4V14"
          stroke="currentColor"
          strokeWidth="1.5"
        />
      </svg>
    ),
    tooltipContent: "Overline",
  },
];
